import { zodResolver } from '@hookform/resolvers/zod';
import type { HeartbeatsHeartbeatWithLastResult, HeartbeatsHttpConfigDTO } from '@smarthome/api';
import {
  useGetApiSettingsHeartbeats,
  usePostApiSettingsHeartbeats,
  usePutApiSettingsHeartbeatsId,
  useDeleteApiSettingsHeartbeatsId,
  useGetApiSettingsHeartbeatsIdLogs,
} from '@smarthome/api';
import { ROUTES } from '@smarthome/ui';
import { formatDistanceToNow } from 'date-fns';
import {
  ArrowLeft,
  Plus,
  Pencil,
  Trash2,
  AlertCircle,
  CheckCircle2,
  XCircle,
  RefreshCw,
  History,
  X,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import {
  Button,
  TextField,
  Select,
  Toggle,
  Modal,
  Badge,
  useToast,
  TextareaField,
} from '@/components/ui';

// HTTP Methods that support body
const BODY_METHODS = ['POST', 'PUT', 'PATCH', 'DELETE'];

// Form schema
const heartbeatSchema = z
  .object({
    name: z.string().min(1, 'Name is required'),
    enabled: z.boolean(),
    type: z.literal('http'),
    intervalSeconds: z.number().min(1, 'Interval must be at least 1 second'),
    retentionMaxEntries: z.number().min(1).nullable(),
    retentionMaxAgeSeconds: z.number().min(1).nullable(),
    http: z.object({
      url: z.string().url('Invalid URL'),
      method: z.enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS']),
      headers: z.array(z.object({ key: z.string(), value: z.string() })),
      body: z.string().nullable(),
    }),
  })
  .refine((data) => data.retentionMaxEntries || data.retentionMaxAgeSeconds, {
    message: 'At least one retention policy is required',
    path: ['retentionMaxEntries'],
  })
  .refine(
    (data) => {
      if (data.http.body && !BODY_METHODS.includes(data.http.method)) {
        return false;
      }
      return true;
    },
    {
      message: 'Body is only allowed for POST, PUT, PATCH, DELETE methods',
      path: ['http', 'body'],
    }
  );

type HeartbeatFormData = z.infer<typeof heartbeatSchema>;

export function HeartbeatsSettingsPage() {
  const { addToast } = useToast();
  const [selectedHeartbeat, setSelectedHeartbeat] =
    useState<HeartbeatsHeartbeatWithLastResult | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [heartbeatToDelete, setHeartbeatToDelete] =
    useState<HeartbeatsHeartbeatWithLastResult | null>(null);
  const [logsHeartbeatId, setLogsHeartbeatId] = useState<string | null>(null);

  const { data, isLoading, isError, refetch } = useGetApiSettingsHeartbeats();

  const { mutate: createHeartbeat, isPending: isCreating } = usePostApiSettingsHeartbeats({
    mutation: {
      onSuccess: () => {
        addToast({ message: 'Heartbeat created', variant: 'success' });
        setIsModalOpen(false);
        refetch();
      },
      onError: (err: Error) => {
        addToast({ message: err.message ?? 'Failed to create heartbeat', variant: 'error' });
      },
    },
  });

  const { mutate: updateHeartbeat, isPending: isUpdating } = usePutApiSettingsHeartbeatsId({
    mutation: {
      onSuccess: () => {
        addToast({ message: 'Heartbeat updated', variant: 'success' });
        setIsModalOpen(false);
        setSelectedHeartbeat(null);
        refetch();
      },
      onError: (err: Error) => {
        addToast({ message: err.message ?? 'Failed to update heartbeat', variant: 'error' });
      },
    },
  });

  const { mutate: deleteHeartbeat, isPending: isDeleting } = useDeleteApiSettingsHeartbeatsId({
    mutation: {
      onSuccess: () => {
        addToast({ message: 'Heartbeat deleted', variant: 'success' });
        setIsDeleteConfirmOpen(false);
        setHeartbeatToDelete(null);
        refetch();
      },
      onError: (err: Error) => {
        addToast({ message: err.message ?? 'Failed to delete heartbeat', variant: 'error' });
      },
    },
  });

  const handleOpenCreate = () => {
    setSelectedHeartbeat(null);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (heartbeat: HeartbeatsHeartbeatWithLastResult) => {
    setSelectedHeartbeat(heartbeat);
    setIsModalOpen(true);
  };

  const handleOpenDelete = (heartbeat: HeartbeatsHeartbeatWithLastResult) => {
    setHeartbeatToDelete(heartbeat);
    setIsDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (heartbeatToDelete?.id) {
      deleteHeartbeat({ id: heartbeatToDelete.id });
    }
  };

  const handleToggleEnabled = (heartbeat: HeartbeatsHeartbeatWithLastResult) => {
    if (!heartbeat.id) return;
    updateHeartbeat({
      id: heartbeat.id,
      data: {
        name: heartbeat.name ?? '',
        enabled: !heartbeat.enabled,
        intervalSeconds: heartbeat.intervalSeconds ?? 60,
        retentionMaxEntries: heartbeat.retentionMaxEntries,
        retentionMaxAgeSeconds: heartbeat.retentionMaxAgeSeconds,
        http: heartbeat.http,
      },
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link
            to={ROUTES.SETTINGS}
            className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-zinc-500 dark:hover:bg-white/[0.04] dark:hover:text-white"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
              Heartbeats
            </h1>
            <p className="mt-1 text-[15px] text-gray-500 dark:text-zinc-500">
              Configure outgoing pings for external monitoring services
            </p>
          </div>
        </div>
        <Button onClick={handleOpenCreate} leftIcon={<Plus className="h-4 w-4" />}>
          Add Heartbeat
        </Button>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
        </div>
      )}

      {/* Error state */}
      {isError && (
        <div className="rounded-lg bg-red-50 p-6 text-center dark:bg-red-500/10">
          <AlertCircle className="mx-auto mb-3 h-8 w-8 text-red-500" />
          <p className="text-sm text-red-600 dark:text-red-400">
            Failed to load heartbeat configurations
          </p>
          <button
            onClick={() => refetch()}
            className="mt-3 text-sm text-blue-500 hover:text-blue-400"
          >
            Try again
          </button>
        </div>
      )}

      {/* Heartbeats list */}
      {!isLoading && !isError && (
        <>
          {data?.heartbeats && data.heartbeats.length > 0 ? (
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.06] dark:bg-[#18181b]">
              {data.heartbeats.map((heartbeat, index) => (
                <HeartbeatRow
                  key={heartbeat.id}
                  heartbeat={heartbeat}
                  index={index}
                  onEdit={() => handleOpenEdit(heartbeat)}
                  onDelete={() => handleOpenDelete(heartbeat)}
                  onToggle={() => handleToggleEnabled(heartbeat)}
                  onViewLogs={() => setLogsHeartbeatId(heartbeat.id ?? null)}
                />
              ))}
            </div>
          ) : (
            <div className="rounded-xl border border-gray-200 bg-white p-12 text-center dark:border-white/[0.06] dark:bg-[#18181b]">
              <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-gray-100 p-3 dark:bg-zinc-800">
                <RefreshCw className="h-6 w-6 text-gray-400 dark:text-zinc-500" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                No heartbeats configured
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-zinc-500">
                Create a heartbeat to start monitoring your application
              </p>
              <Button
                className="mt-4"
                onClick={handleOpenCreate}
                leftIcon={<Plus className="h-4 w-4" />}
              >
                Add Heartbeat
              </Button>
            </div>
          )}
        </>
      )}

      {/* Create/Edit Modal */}
      <HeartbeatModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedHeartbeat(null);
        }}
        heartbeat={selectedHeartbeat}
        onSubmit={(data) => {
          const httpConfig: HeartbeatsHttpConfigDTO = {
            url: data.http.url,
            method: data.http.method,
            headers: data.http.headers.reduce(
              (acc, h) => {
                if (h.key) acc[h.key] = h.value;
                return acc;
              },
              {} as Record<string, string>
            ),
            body: BODY_METHODS.includes(data.http.method)
              ? (data.http.body ?? undefined)
              : undefined,
          };

          if (selectedHeartbeat?.id) {
            updateHeartbeat({
              id: selectedHeartbeat.id,
              data: {
                name: data.name,
                enabled: data.enabled,
                intervalSeconds: data.intervalSeconds,
                retentionMaxEntries: data.retentionMaxEntries ?? undefined,
                retentionMaxAgeSeconds: data.retentionMaxAgeSeconds ?? undefined,
                http: httpConfig,
              },
            });
          } else {
            createHeartbeat({
              data: {
                name: data.name,
                enabled: data.enabled,
                type: 'http',
                intervalSeconds: data.intervalSeconds,
                retentionMaxEntries: data.retentionMaxEntries ?? undefined,
                retentionMaxAgeSeconds: data.retentionMaxAgeSeconds ?? undefined,
                http: httpConfig,
              },
            });
          }
        }}
        isSaving={isCreating || isUpdating}
      />

      {/* Delete Confirmation */}
      <Modal
        isOpen={isDeleteConfirmOpen}
        onClose={() => {
          setIsDeleteConfirmOpen(false);
          setHeartbeatToDelete(null);
        }}
        title="Delete Heartbeat"
        subtitle="This action cannot be undone"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setIsDeleteConfirmOpen(false);
                setHeartbeatToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button variant="danger" onClick={handleConfirmDelete} loading={isDeleting}>
              Delete
            </Button>
          </>
        }
      >
        <p className="text-sm text-gray-600 dark:text-zinc-400">
          Are you sure you want to delete <strong>{heartbeatToDelete?.name}</strong>? All associated
          logs will also be deleted.
        </p>
      </Modal>

      {/* Logs Modal */}
      {logsHeartbeatId && (
        <LogsModal
          heartbeatId={logsHeartbeatId}
          heartbeatName={data?.heartbeats?.find((h) => h.id === logsHeartbeatId)?.name ?? 'Unknown'}
          onClose={() => setLogsHeartbeatId(null)}
        />
      )}
    </div>
  );
}

// Heartbeat Row Component
function HeartbeatRow({
  heartbeat,
  index,
  onEdit,
  onDelete,
  onToggle,
  onViewLogs,
}: {
  heartbeat: HeartbeatsHeartbeatWithLastResult;
  index: number;
  onEdit: () => void;
  onDelete: () => void;
  onToggle: () => void;
  onViewLogs: () => void;
}) {
  const lastResult = heartbeat.lastResult;
  const hasLastResult = lastResult?.timestamp;

  return (
    <div
      className={`flex items-center justify-between p-4 ${
        index !== 0 ? 'border-t border-gray-200 dark:border-white/[0.06]' : ''
      }`}
    >
      <div className="flex items-center gap-4">
        <Toggle label="" checked={heartbeat.enabled ?? false} onChange={onToggle} />
        <div>
          <div className="flex items-center gap-2">
            <span className="text-[15px] font-medium text-gray-900 dark:text-white">
              {heartbeat.name}
            </span>
            <Badge variant="default" size="sm">
              {heartbeat.type?.toUpperCase()}
            </Badge>
          </div>
          <div className="mt-0.5 flex items-center gap-2 text-sm text-gray-500 dark:text-zinc-500">
            <span>{heartbeat.http?.method}</span>
            <span className="text-gray-300 dark:text-zinc-700">•</span>
            <span className="truncate max-w-[300px]">{heartbeat.http?.url}</span>
            <span className="text-gray-300 dark:text-zinc-700">•</span>
            <span>Every {formatInterval(heartbeat.intervalSeconds ?? 0)}</span>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3">
        {/* Last result */}
        {hasLastResult && (
          <div className="flex items-center gap-2">
            {lastResult.success ? (
              <Badge variant="success" size="sm" className="flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3" />
                OK
                {lastResult.responseCode && ` (${lastResult.responseCode})`}
              </Badge>
            ) : (
              <Badge variant="error" size="sm" className="flex items-center gap-1">
                <XCircle className="h-3 w-3" />
                FAIL
                {lastResult.responseCode && ` (${lastResult.responseCode})`}
              </Badge>
            )}
            <span className="text-xs text-gray-400 dark:text-zinc-600">
              {formatDistanceToNow(new Date(lastResult.timestamp!), { addSuffix: true })}
            </span>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-1">
          <button
            onClick={onViewLogs}
            className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:text-zinc-500 dark:hover:bg-white/[0.04] dark:hover:text-zinc-300"
            title="View logs"
          >
            <History className="h-4 w-4" />
          </button>
          <button
            onClick={onEdit}
            className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:text-zinc-500 dark:hover:bg-white/[0.04] dark:hover:text-zinc-300"
            title="Edit"
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            onClick={onDelete}
            className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500 dark:text-zinc-500 dark:hover:bg-red-500/10 dark:hover:text-red-400"
            title="Delete"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// Heartbeat Form Modal
function HeartbeatModal({
  isOpen,
  onClose,
  heartbeat,
  onSubmit,
  isSaving,
}: {
  isOpen: boolean;
  onClose: () => void;
  heartbeat: HeartbeatsHeartbeatWithLastResult | null;
  onSubmit: (data: HeartbeatFormData) => void;
  isSaving: boolean;
}) {
  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<HeartbeatFormData>({
    resolver: zodResolver(heartbeatSchema),
    defaultValues: {
      name: '',
      enabled: true,
      type: 'http',
      intervalSeconds: 60,
      retentionMaxEntries: 100,
      retentionMaxAgeSeconds: null,
      http: {
        url: '',
        method: 'GET',
        headers: [],
        body: null,
      },
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'http.headers',
  });

  const watchMethod = watch('http.method');
  const showBody = BODY_METHODS.includes(watchMethod);

  // Reset form when heartbeat changes
  useEffect(() => {
    if (isOpen) {
      if (heartbeat) {
        const headers = heartbeat.http?.headers
          ? Object.entries(heartbeat.http.headers).map(([key, value]) => ({ key, value }))
          : [];
        reset({
          name: heartbeat.name ?? '',
          enabled: heartbeat.enabled ?? false,
          type: 'http',
          intervalSeconds: heartbeat.intervalSeconds ?? 60,
          retentionMaxEntries: heartbeat.retentionMaxEntries ?? null,
          retentionMaxAgeSeconds: heartbeat.retentionMaxAgeSeconds ?? null,
          http: {
            url: heartbeat.http?.url ?? '',
            method: (heartbeat.http?.method as HeartbeatFormData['http']['method']) ?? 'GET',
            headers,
            body: heartbeat.http?.body ?? null,
          },
        });
      } else {
        reset({
          name: '',
          enabled: true,
          type: 'http',
          intervalSeconds: 60,
          retentionMaxEntries: 100,
          retentionMaxAgeSeconds: null,
          http: {
            url: '',
            method: 'GET',
            headers: [],
            body: null,
          },
        });
      }
    }
  }, [heartbeat, isOpen, reset]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={heartbeat ? 'Edit Heartbeat' : 'Add Heartbeat'}
      subtitle="Configure an outgoing HTTP ping"
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="heartbeat-form" loading={isSaving}>
            {heartbeat ? 'Save Changes' : 'Create Heartbeat'}
          </Button>
        </>
      }
    >
      <form id="heartbeat-form" onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {/* Basic Info */}
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField label="Name" {...register('name')} error={errors.name?.message} />
          <Controller
            name="enabled"
            control={control}
            render={({ field }) => (
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300">
                  Status
                </label>
                <div className="flex items-center gap-2 pt-2">
                  <Toggle
                    label={field.value ? 'Enabled' : 'Disabled'}
                    checked={field.value}
                    onChange={field.onChange}
                  />
                </div>
              </div>
            )}
          />
        </div>

        {/* Interval */}
        <TextField
          label="Interval (seconds)"
          type="number"
          {...register('intervalSeconds', { valueAsNumber: true })}
          error={errors.intervalSeconds?.message}
        />

        {/* Retention */}
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            label="Max log entries"
            type="number"
            {...register('retentionMaxEntries', {
              setValueAs: (v) => (v === '' ? null : Number(v)),
            })}
            error={errors.retentionMaxEntries?.message}
            placeholder="e.g., 100"
          />
          <TextField
            label="Max log age (seconds)"
            type="number"
            {...register('retentionMaxAgeSeconds', {
              setValueAs: (v) => (v === '' ? null : Number(v)),
            })}
            error={errors.retentionMaxAgeSeconds?.message}
            placeholder="e.g., 86400"
          />
        </div>

        <hr className="border-gray-200 dark:border-white/[0.06]" />

        {/* HTTP Config */}
        <h3 className="text-sm font-medium text-gray-900 dark:text-white">HTTP Configuration</h3>

        <TextField
          label="URL"
          {...register('http.url')}
          error={errors.http?.url?.message}
          placeholder="https://example.com/heartbeat"
        />

        <Controller
          name="http.method"
          control={control}
          render={({ field }) => (
            <Select
              label="Method"
              value={field.value}
              onChange={field.onChange}
              options={[
                { value: 'GET', label: 'GET' },
                { value: 'POST', label: 'POST' },
                { value: 'PUT', label: 'PUT' },
                { value: 'PATCH', label: 'PATCH' },
                { value: 'DELETE', label: 'DELETE' },
                { value: 'HEAD', label: 'HEAD' },
                { value: 'OPTIONS', label: 'OPTIONS' },
              ]}
              error={errors.http?.method?.message}
            />
          )}
        />

        {/* Headers */}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300">
            Headers
          </label>
          <div className="space-y-2">
            {fields.map((field, index) => (
              <div key={field.id} className="flex items-center gap-2">
                <input
                  {...register(`http.headers.${index}.key`)}
                  placeholder="Header name"
                  className="flex-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-white/[0.08] dark:bg-zinc-900 dark:text-white dark:placeholder-zinc-500"
                />
                <input
                  {...register(`http.headers.${index}.value`)}
                  placeholder="Value"
                  className="flex-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-white/[0.08] dark:bg-zinc-900 dark:text-white dark:placeholder-zinc-500"
                />
                <button
                  type="button"
                  onClick={() => remove(index)}
                  className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-white/[0.04] dark:hover:text-zinc-300"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => append({ key: '', value: '' })}
            >
              Add Header
            </Button>
          </div>
        </div>

        {/* Body (conditional) */}
        {showBody && (
          <TextareaField
            label="Body"
            {...register('http.body')}
            error={errors.http?.body?.message}
            placeholder='{"status": "alive"}'
            rows={4}
          />
        )}
      </form>
    </Modal>
  );
}

// Logs Modal
function LogsModal({
  heartbeatId,
  heartbeatName,
  onClose,
}: {
  heartbeatId: string;
  heartbeatName: string;
  onClose: () => void;
}) {
  const { data, isLoading, refetch } = useGetApiSettingsHeartbeatsIdLogs(heartbeatId, {
    limit: 50,
  });

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={`Logs: ${heartbeatName}`}
      subtitle="Recent heartbeat execution logs"
      size="lg"
      footer={
        <div className="flex items-center justify-between w-full">
          <Button
            variant="outline"
            onClick={() => refetch()}
            leftIcon={<RefreshCw className="h-4 w-4" />}
          >
            Refresh
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      }
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
        </div>
      ) : data?.logs && data.logs.length > 0 ? (
        <div className="max-h-[400px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white dark:bg-zinc-900">
              <tr className="border-b border-gray-200 dark:border-white/[0.06]">
                <th className="py-2 pr-4 text-left font-medium text-gray-500 dark:text-zinc-500">
                  Time
                </th>
                <th className="py-2 pr-4 text-left font-medium text-gray-500 dark:text-zinc-500">
                  Status
                </th>
                <th className="py-2 pr-4 text-left font-medium text-gray-500 dark:text-zinc-500">
                  Code
                </th>
                <th className="py-2 pr-4 text-left font-medium text-gray-500 dark:text-zinc-500">
                  Duration
                </th>
                <th className="py-2 text-left font-medium text-gray-500 dark:text-zinc-500">
                  Error
                </th>
              </tr>
            </thead>
            <tbody>
              {data.logs.map((log) => (
                <tr key={log.id} className="border-b border-gray-100 dark:border-white/[0.03]">
                  <td className="py-2 pr-4 text-gray-600 dark:text-zinc-400">
                    {log.timestamp ? new Date(log.timestamp).toLocaleString() : '-'}
                  </td>
                  <td className="py-2 pr-4">
                    {log.success ? (
                      <Badge variant="success" size="sm">
                        OK
                      </Badge>
                    ) : (
                      <Badge variant="error" size="sm">
                        FAIL
                      </Badge>
                    )}
                  </td>
                  <td className="py-2 pr-4 text-gray-600 dark:text-zinc-400">
                    {log.responseCode ?? '-'}
                  </td>
                  <td className="py-2 pr-4 text-gray-600 dark:text-zinc-400">{log.durationMs}ms</td>
                  <td className="py-2 text-red-500 dark:text-red-400 max-w-[200px] truncate">
                    {log.error ?? '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="py-8 text-center text-sm text-gray-500 dark:text-zinc-500">
          No logs available
        </div>
      )}
    </Modal>
  );
}

// Helper to format interval
function formatInterval(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86400)}d`;
}
